// VRP Data Context - Unified Server State Management
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import { useVRPToolpadNavigation } from '../providers/ToolpadAppProvider';
import type {
  VRPDataState,
  EntityTypeName,
  VRPEntityTypes,
  EntityQueryOptions,
  EntityMutationOptions,
  BulkOperationOptions
} from '../types/data';

interface VRPDataContextType {
  // Entity Collections
  entities: VRPDataState['entities'];
  
  // Loading States
  loading: VRPDataState['loading'];
  
  // Error States
  errors: VRPDataState['errors'];
  
  // Cache Information
  cache: VRPDataState['cache'];
  
  // CRUD Operations
  create: <T extends EntityTypeName>(
    entityType: T,
    data: Omit<VRPEntityTypes[T], 'id' | 'created_at' | 'updated_at'>,
    options?: EntityMutationOptions
  ) => Promise<VRPEntityTypes[T]>;
  
  update: <T extends EntityTypeName>(
    entityType: T,
    id: number,
    data: Partial<VRPEntityTypes[T]>,
    options?: EntityMutationOptions
  ) => Promise<VRPEntityTypes[T]>;
  
  delete: <T extends EntityTypeName>(
    entityType: T,
    id: number,
    options?: EntityMutationOptions
  ) => Promise<void>;
  
  // Bulk Operations
  bulkCreate: <T extends EntityTypeName>(
    entityType: T,
    items: Array<Omit<VRPEntityTypes[T], 'id' | 'created_at' | 'updated_at'>>,
    options?: BulkOperationOptions
  ) => Promise<VRPEntityTypes[T][]>;
  
  bulkUpdate: <T extends EntityTypeName>(
    entityType: T,
    updates: Array<{ id: number; data: Partial<VRPEntityTypes[T]> }>,
    options?: BulkOperationOptions
  ) => Promise<VRPEntityTypes[T][]>;
  
  bulkDelete: <T extends EntityTypeName>(
    entityType: T,
    ids: number[],
    options?: BulkOperationOptions
  ) => Promise<void>;
  
  // Utility Methods
  refresh: <T extends EntityTypeName>(entityType: T) => Promise<void>;
  invalidate: <T extends EntityTypeName>(entityType: T) => void;
  prefetch: <T extends EntityTypeName>(entityType: T, options?: EntityQueryOptions) => Promise<void>;
}

const VRPDataContext = createContext<VRPDataContextType | null>(null);

interface VRPDataProviderProps {
  children: ReactNode;
}

export const VRPDataProvider: React.FC<VRPDataProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  
  // Use Toolpad navigation context for proper navigation state
  const toolpadNavigation = useVRPToolpadNavigation();
  const navigation = toolpadNavigation?.navigation || {};
  
  // Extract project/scenario/dataset context from navigation state with null safety
  // Parse IDs from navigation state (they come as strings from URL params)
  const currentProject = navigation?.currentProject ? {
    id: parseInt(navigation.currentProject, 10),
    name: `Project ${navigation.currentProject}` // Will be enriched with real data later
  } : null;
  
  const currentScenario = navigation?.currentScenario ? {
    id: parseInt(navigation.currentScenario, 10),
    project_id: currentProject?.id || null,
    name: `Scenario ${navigation.currentScenario}` // Will be enriched with real data later
  } : null;
  
  const currentDataset = navigation?.currentDataset ? {
    id: parseInt(navigation.currentDataset, 10),
    project_id: currentProject?.id || null,
    scenario_id: currentScenario?.id || null,
    name: `Dataset ${navigation.currentDataset}` // Will be enriched with real data later
  } : null;

  // Safe query enablement - prevent queries when no context available
  const hasProjectContext = Boolean(currentProject?.id && !isNaN(currentProject.id));
  const hasScenarioContext = Boolean(currentScenario?.id && !isNaN(currentScenario.id));

  console.log('[VRPDataProvider] Navigation context updated:', {
    currentProject,
    currentScenario,
    currentDataset,
    hasProjectContext,
    hasScenarioContext,
    pathname: navigation?.pathname || 'unknown'
  });

  // Query configurations for different entity types
  const getQueryConfig = <T extends EntityTypeName>(entityType: T) => {
    const projectId = currentProject?.id;
    const scenarioId = currentScenario?.id;
    const datasetId = currentDataset?.id;

    // Enhanced safety checks to prevent queries when contexts are invalid
    const safeProjectEnabled = hasProjectContext && projectId > 0;
    const safeScenarioEnabled = hasScenarioContext && scenarioId > 0;

    switch (entityType) {
      case 'vehicles':
        return {
          queryKey: ['vehicles', projectId],
          queryFn: () => safeProjectEnabled ? apiClient.vehicles.getAll(projectId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1, // Reduce retries for failed auth queries
          retryDelay: 1000
        };
      
      case 'locations':
        return {
          queryKey: ['locations', projectId],
          queryFn: () => safeProjectEnabled ? apiClient.locations.getAll(projectId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'jobs':
        return {
          queryKey: ['jobs', projectId, scenarioId],
          queryFn: () => safeProjectEnabled ? apiClient.jobs.getAll(projectId, scenarioId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'products':
        return {
          queryKey: ['products', projectId],
          queryFn: () => safeProjectEnabled ? apiClient.products.getAll(projectId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'shipments':
        return {
          queryKey: ['shipments', projectId, scenarioId],
          queryFn: () => safeProjectEnabled ? apiClient.shipments.getAll(projectId, scenarioId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'skills':
        return {
          queryKey: ['skills', projectId],
          queryFn: () => safeProjectEnabled ? apiClient.skills.getAll(projectId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'scenarios':
        return {
          queryKey: ['scenarios', projectId],
          queryFn: () => safeProjectEnabled ? apiClient.scenarios.getAll(projectId) : Promise.resolve([]),
          enabled: safeProjectEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'datasets':
        return {
          queryKey: ['datasets', projectId, scenarioId],
          queryFn: () => (safeProjectEnabled && safeScenarioEnabled) ? apiClient.datasets.getAll(projectId, scenarioId) : Promise.resolve([]),
          enabled: safeProjectEnabled && safeScenarioEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'optimizations':
        return {
          queryKey: ['optimizations', projectId, scenarioId],
          queryFn: () => (safeProjectEnabled && safeScenarioEnabled) ? apiClient.optimizations.getAll(projectId, scenarioId) : Promise.resolve([]),
          enabled: safeProjectEnabled && safeScenarioEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      case 'routes':
        return {
          queryKey: ['routes', projectId, scenarioId],
          queryFn: () => (safeProjectEnabled && safeScenarioEnabled) ? apiClient.routes.getAll(projectId, scenarioId) : Promise.resolve([]),
          enabled: safeProjectEnabled && safeScenarioEnabled,
          retry: 1,
          retryDelay: 1000
        };
      
      default:
        return {
          queryKey: [entityType, projectId],
          queryFn: () => Promise.resolve([]),
          enabled: false
        };
    }
  };

  // Query hooks for all entity types
  const entityQueries = {
    vehicles: useQuery({
      ...getQueryConfig('vehicles'),
      staleTime: 5 * 60 * 1000
    }),
    locations: useQuery({
      ...getQueryConfig('locations'),
      staleTime: 5 * 60 * 1000
    }),
    jobs: useQuery({
      ...getQueryConfig('jobs'),
      staleTime: 3 * 60 * 1000
    }),
    products: useQuery({
      ...getQueryConfig('products'),
      staleTime: 10 * 60 * 1000
    }),
    shipments: useQuery({
      ...getQueryConfig('shipments'),
      staleTime: 3 * 60 * 1000
    }),
    skills: useQuery({
      ...getQueryConfig('skills'),
      staleTime: 10 * 60 * 1000
    }),
    scenarios: useQuery({
      ...getQueryConfig('scenarios'),
      staleTime: 5 * 60 * 1000
    }),
    datasets: useQuery({
      ...getQueryConfig('datasets'),
      staleTime: 5 * 60 * 1000
    }),
    optimizations: useQuery({
      ...getQueryConfig('optimizations'),
      staleTime: 1 * 60 * 1000
    }),
    routes: useQuery({
      ...getQueryConfig('routes'),
      staleTime: 1 * 60 * 1000
    })
  };

  // Aggregated state
  const entities = useMemo(() => ({
    vehicles: entityQueries.vehicles.data || [],
    locations: entityQueries.locations.data || [],
    jobs: entityQueries.jobs.data || [],
    products: entityQueries.products.data || [],
    shipments: entityQueries.shipments.data || [],
    skills: entityQueries.skills.data || [],
    scenarios: entityQueries.scenarios.data || [],
    datasets: entityQueries.datasets.data || [],
    optimizations: entityQueries.optimizations.data || [],
    routes: entityQueries.routes.data || []
  }), [entityQueries]);

  const loading = useMemo(() => {
    // Only consider enabled queries for loading state calculation
    const enabledQueries = Object.entries(entityQueries).filter(([key, query]) => {
      const config = getQueryConfig(key as EntityTypeName);
      return config.enabled; // Only count enabled queries
    });

    // If no queries are enabled (e.g., Table Editor with no project context), 
    // don't show as initializing to prevent infinite loading
    const anyQueriesEnabled = enabledQueries.length > 0;
    const anyEnabledLoading = enabledQueries.some(([, query]) => query.isLoading && query.isFetching);

    return {
      vehicles: entityQueries.vehicles.isLoading,
      locations: entityQueries.locations.isLoading,
      jobs: entityQueries.jobs.isLoading,
      products: entityQueries.products.isLoading,
      shipments: entityQueries.shipments.isLoading,
      skills: entityQueries.skills.isLoading,
      scenarios: entityQueries.scenarios.isLoading,
      datasets: entityQueries.datasets.isLoading,
      optimizations: entityQueries.optimizations.isLoading,
      routes: entityQueries.routes.isLoading,
      // Only show as initializing if we have enabled queries that are loading
      initializing: anyQueriesEnabled && anyEnabledLoading,
      mutations: {
        vehicles: { creating: false, updating: new Set(), deleting: new Set() },
        locations: { creating: false, updating: new Set(), deleting: new Set() },
        jobs: { creating: false, updating: new Set(), deleting: new Set() },
        products: { creating: false, updating: new Set(), deleting: new Set() },
        shipments: { creating: false, updating: new Set(), deleting: new Set() },
        skills: { creating: false, updating: new Set(), deleting: new Set() },
        scenarios: { creating: false, updating: new Set(), deleting: new Set() },
        datasets: { creating: false, updating: new Set(), deleting: new Set() },
        optimizations: { creating: false, updating: new Set(), deleting: new Set() },
        routes: { creating: false, updating: new Set(), deleting: new Set() }
      }
    };
  }, [entityQueries]);

  const errors = useMemo(() => {
    // Filter out authentication errors for disabled queries to prevent hanging
    const getFilteredError = (query: any, entityType: EntityTypeName) => {
      const config = getQueryConfig(entityType);
      const error = query.error as Error | null;
      
      // If query is disabled, don't report errors
      if (!config.enabled) {
        return null;
      }
      
      // If it's an auth error (400/401/403), log it but don't hang the context
      if (error && error.message?.includes('40')) {
        console.warn(`[VRPDataProvider] Auth error for ${entityType} (context may not be ready):`, error.message);
        return null; // Don't propagate auth errors when context isn't ready
      }
      
      return error;
    };

    return {
      vehicles: getFilteredError(entityQueries.vehicles, 'vehicles'),
      locations: getFilteredError(entityQueries.locations, 'locations'),
      jobs: getFilteredError(entityQueries.jobs, 'jobs'),
      products: getFilteredError(entityQueries.products, 'products'),
      shipments: getFilteredError(entityQueries.shipments, 'shipments'),
      skills: getFilteredError(entityQueries.skills, 'skills'),
      scenarios: getFilteredError(entityQueries.scenarios, 'scenarios'),
      datasets: getFilteredError(entityQueries.datasets, 'datasets'),
      optimizations: getFilteredError(entityQueries.optimizations, 'optimizations'),
      routes: getFilteredError(entityQueries.routes, 'routes'),
      mutations: {
        vehicles: null,
        locations: null,
        jobs: null,
        products: null,
        shipments: null,
        skills: null,
        scenarios: null,
        datasets: null,
        optimizations: null,
        routes: null
      }
    };
  }, [entityQueries]);

  const cache = useMemo(() => ({
    vehicles: {
      lastFetch: entityQueries.vehicles.dataUpdatedAt || null,
      isStale: entityQueries.vehicles.isStale,
      queryKey: getQueryConfig('vehicles').queryKey
    },
    locations: {
      lastFetch: entityQueries.locations.dataUpdatedAt || null,
      isStale: entityQueries.locations.isStale,
      queryKey: getQueryConfig('locations').queryKey
    },
    jobs: {
      lastFetch: entityQueries.jobs.dataUpdatedAt || null,
      isStale: entityQueries.jobs.isStale,
      queryKey: getQueryConfig('jobs').queryKey
    },
    products: {
      lastFetch: entityQueries.products.dataUpdatedAt || null,
      isStale: entityQueries.products.isStale,
      queryKey: getQueryConfig('products').queryKey
    },
    shipments: {
      lastFetch: entityQueries.shipments.dataUpdatedAt || null,
      isStale: entityQueries.shipments.isStale,
      queryKey: getQueryConfig('shipments').queryKey
    },
    skills: {
      lastFetch: entityQueries.skills.dataUpdatedAt || null,
      isStale: entityQueries.skills.isStale,
      queryKey: getQueryConfig('skills').queryKey
    },
    scenarios: {
      lastFetch: entityQueries.scenarios.dataUpdatedAt || null,
      isStale: entityQueries.scenarios.isStale,
      queryKey: getQueryConfig('scenarios').queryKey
    },
    datasets: {
      lastFetch: entityQueries.datasets.dataUpdatedAt || null,
      isStale: entityQueries.datasets.isStale,
      queryKey: getQueryConfig('datasets').queryKey
    },
    optimizations: {
      lastFetch: entityQueries.optimizations.dataUpdatedAt || null,
      isStale: entityQueries.optimizations.isStale,
      queryKey: getQueryConfig('optimizations').queryKey
    },
    routes: {
      lastFetch: entityQueries.routes.dataUpdatedAt || null,
      isStale: entityQueries.routes.isStale,
      queryKey: getQueryConfig('routes').queryKey
    }
  }), [entityQueries, currentProject, currentScenario]);

  // CRUD Operations
  const create = async <T extends EntityTypeName>(
    entityType: T,
    data: Omit<VRPEntityTypes[T], 'id' | 'created_at' | 'updated_at'>,
    options?: EntityMutationOptions
  ): Promise<VRPEntityTypes[T]> => {
    if (!currentProject) {
      throw new Error('No project context available for creating entity');
    }

    // Get appropriate API method
    const apiMethod = getApiMethod(entityType, 'create');
    const result = await apiMethod(currentProject.id, data);

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: getQueryConfig(entityType).queryKey });
    
    if (options?.invalidateQueries) {
      options.invalidateQueries.forEach(queryKey => {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      });
    }

    options?.onSuccess?.(result);
    return result;
  };

  const update = async <T extends EntityTypeName>(
    entityType: T,
    id: number,
    data: Partial<VRPEntityTypes[T]>,
    options?: EntityMutationOptions
  ): Promise<VRPEntityTypes[T]> => {
    if (!currentProject) {
      throw new Error('No project context available for updating entity');
    }

    const apiMethod = getApiMethod(entityType, 'update');
    const result = await apiMethod(currentProject.id, id, data);

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: getQueryConfig(entityType).queryKey });
    
    options?.onSuccess?.(result);
    return result;
  };

  const deleteEntity = async <T extends EntityTypeName>(
    entityType: T,
    id: number,
    options?: EntityMutationOptions
  ): Promise<void> => {
    if (!currentProject) {
      throw new Error('No project context available for deleting entity');
    }

    const apiMethod = getApiMethod(entityType, 'delete');
    await apiMethod(currentProject.id, id);

    // Invalidate queries
    queryClient.invalidateQueries({ queryKey: getQueryConfig(entityType).queryKey });
    
    options?.onSuccess?.(undefined);
  };

  // Bulk operations
  const bulkCreate = async <T extends EntityTypeName>(
    entityType: T,
    items: Array<Omit<VRPEntityTypes[T], 'id' | 'created_at' | 'updated_at'>>,
    options?: BulkOperationOptions
  ): Promise<VRPEntityTypes[T][]> => {
    if (!currentProject) {
      throw new Error('No project context available for bulk create');
    }

    const results: VRPEntityTypes[T][] = [];
    const batchSize = options?.batchSize || 10;
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      
      if (options?.parallel) {
        const batchResults = await Promise.all(
          batch.map(item => create(entityType, item))
        );
        results.push(...batchResults);
      } else {
        for (const item of batch) {
          try {
            const result = await create(entityType, item);
            results.push(result);
            options?.onProgress?.(results.length, items.length);
          } catch (error) {
            if (options?.stopOnError) {
              throw error;
            }
            console.error(`Failed to create ${entityType}:`, error);
          }
        }
      }
    }

    return results;
  };

  const bulkUpdate = async <T extends EntityTypeName>(
    entityType: T,
    updates: Array<{ id: number; data: Partial<VRPEntityTypes[T]> }>,
    options?: BulkOperationOptions
  ): Promise<VRPEntityTypes[T][]> => {
    if (!currentProject) {
      throw new Error('No project context available for bulk update');
    }

    const results: VRPEntityTypes[T][] = [];
    const batchSize = options?.batchSize || 10;
    
    for (let i = 0; i < updates.length; i += batchSize) {
      const batch = updates.slice(i, i + batchSize);
      
      if (options?.parallel) {
        const batchResults = await Promise.all(
          batch.map(({ id, data }) => update(entityType, id, data))
        );
        results.push(...batchResults);
      } else {
        for (const { id, data } of batch) {
          try {
            const result = await update(entityType, id, data);
            results.push(result);
            options?.onProgress?.(results.length, updates.length);
          } catch (error) {
            if (options?.stopOnError) {
              throw error;
            }
            console.error(`Failed to update ${entityType} ${id}:`, error);
          }
        }
      }
    }

    return results;
  };

  const bulkDelete = async <T extends EntityTypeName>(
    entityType: T,
    ids: number[],
    options?: BulkOperationOptions
  ): Promise<void> => {
    if (!currentProject) {
      throw new Error('No project context available for bulk delete');
    }

    const batchSize = options?.batchSize || 10;
    let completed = 0;
    
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      
      if (options?.parallel) {
        await Promise.all(
          batch.map(id => deleteEntity(entityType, id))
        );
        completed += batch.length;
      } else {
        for (const id of batch) {
          try {
            await deleteEntity(entityType, id);
            completed++;
            options?.onProgress?.(completed, ids.length);
          } catch (error) {
            if (options?.stopOnError) {
              throw error;
            }
            console.error(`Failed to delete ${entityType} ${id}:`, error);
          }
        }
      }
    }
  };

  // Utility methods
  const refresh = async <T extends EntityTypeName>(entityType: T): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: getQueryConfig(entityType).queryKey });
  };

  const invalidate = <T extends EntityTypeName>(entityType: T): void => {
    queryClient.invalidateQueries({ queryKey: getQueryConfig(entityType).queryKey });
  };

  const prefetch = async <T extends EntityTypeName>(
    entityType: T,
    options?: EntityQueryOptions
  ): Promise<void> => {
    const config = getQueryConfig(entityType);
    await queryClient.prefetchQuery({
      ...config,
      ...options
    });
  };

  const value: VRPDataContextType = useMemo(() => ({
    entities,
    loading,
    errors,
    cache,
    create,
    update,
    delete: deleteEntity,
    bulkCreate,
    bulkUpdate,
    bulkDelete,
    refresh,
    invalidate,
    prefetch
  }), [
    entities,
    loading,
    errors,
    cache,
    create,
    update,
    deleteEntity,
    bulkCreate,
    bulkUpdate,
    bulkDelete,
    refresh,
    invalidate,
    prefetch
  ]);

  return (
    <VRPDataContext.Provider value={value}>
      {children}
    </VRPDataContext.Provider>
  );
};

export const useVRPData = (): VRPDataContextType => {
  const context = useContext(VRPDataContext);
  if (!context) {
    // Enhanced error message to help debug React Error #31
    console.error('[VRPDataContext] CRITICAL: useVRPData called outside of provider context');
    console.error('[VRPDataContext] This may cause React Error #31 in production');
    console.error('[VRPDataContext] Component call stack:', new Error().stack);
    throw new Error('useVRPData must be used within a VRPDataProvider');
  }
  
  // Additional defensive validation
  if (typeof context !== 'object' || context === null) {
    console.error('[VRPDataContext] CRITICAL: Invalid context object received');
    throw new Error('VRPDataContext is corrupted');
  }
  
  return context;
};

// Helper function to get appropriate API method
function getApiMethod(entityType: EntityTypeName, operation: 'create' | 'update' | 'delete') {
  const apiMap: Record<EntityTypeName, any> = {
    vehicles: apiClient.vehicles,
    locations: apiClient.locations,
    jobs: apiClient.jobs,
    products: apiClient.products,
    shipments: apiClient.shipments,
    skills: apiClient.skills,
    scenarios: apiClient.scenarios,
    datasets: apiClient.datasets,
    optimizations: apiClient.optimizations,
    routes: apiClient.routes
  };

  const entityApi = apiMap[entityType];
  if (!entityApi) {
    throw new Error(`No API configured for entity type: ${entityType}`);
  }

  return entityApi[operation];
}

// Convenience hooks for individual entities
export const useVehicles = () => {
  const { entities, loading, errors } = useVRPData();
  return {
    vehicles: entities.vehicles,
    isLoading: loading.vehicles,
    error: errors.vehicles
  };
};

export const useLocations = () => {
  const { entities, loading, errors } = useVRPData();
  return {
    locations: entities.locations,
    isLoading: loading.locations,
    error: errors.locations
  };
};

export const useJobs = () => {
  const { entities, loading, errors } = useVRPData();
  return {
    jobs: entities.jobs,
    isLoading: loading.jobs,
    error: errors.jobs
  };
};